import type { ButtonHTMLAttributes, ReactNode } from "react";

type ActionButtonVariant = "primary" | "secondary" | "ghost" | "danger";
type ActionButtonSize = "sm" | "md";

interface ActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ActionButtonVariant;
  size?: ActionButtonSize;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
}

const VARIANT_CLASS: Record<ActionButtonVariant, string> = {
  primary:
    "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700 shadow-sm shadow-brand-500/20",
  secondary:
    "border border-brand-200 bg-surface text-brand-600 hover:bg-brand-50 dark:border-brand-500/30 dark:text-brand-200 dark:hover:bg-brand-500/10",
  ghost:
    "bg-transparent text-gray-600 hover:bg-surface-hover dark:text-gray-300",
  danger:
    "bg-danger-600 text-white hover:bg-danger-700 active:bg-danger-800 shadow-sm shadow-danger-600/20",
};

const SIZE_CLASS: Record<ActionButtonSize, string> = {
  sm: "rounded-xl px-3 py-2 text-sm font-medium",
  md: "rounded-2xl px-4 py-2.5 text-sm font-semibold",
};

const ActionButton = ({
  variant = "primary",
  size = "md",
  startIcon,
  endIcon,
  className = "",
  children,
  type = "button",
  ...rest
}: ActionButtonProps): JSX.Element => {
  return (
    <button
      type={type}
      className={`inline-flex items-center justify-center gap-2 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${SIZE_CLASS[size]} ${VARIANT_CLASS[variant]} ${className}`.trim()}
      {...rest}
    >
      {startIcon}
      {children}
      {endIcon}
    </button>
  );
};

export default ActionButton;